/**
 * Verdichten einer Energiereihe auf ein gröberes Raster (HM-012).
 *
 * Eine **Energie**reihe trägt je Bucket eine Menge; das gröbere Raster ist die
 * Summe seiner Teile. Zwei Dinge daran sind fachlich und nicht technisch:
 *
 * **Fehlt ein Teil, ist die Summe unbekannt — nicht kleiner.** Ein Tag, dem
 * eine Viertelstunde fehlt, hat keinen Tagesverbrauch, sondern eine Lücke
 * (Regel 8). Dieselbe Regel gilt beim Stapeln im Diagramm (ADR 0021); eine
 * Tagessumme über 95 Viertelstunden sähe aus wie ein Tag mit etwas weniger
 * Verbrauch, und niemand sähe ihr das an. Was trotzdem über ein lückenhaftes
 * Fenster gesagt werden kann, sagt `sumWithCoverage` — mit der Abdeckung dabei.
 *
 * **Ein Tag ist ein lokaler Tag.** `resampleToLocalDays` schneidet an
 * `startOfLocalDay` in der Zeitzone der Anlage und erwartet je Tag so viele
 * Buckets, wie er lang ist — 92 oder 100 an den Umstellungstagen (Regel 3).
 */
import type { ValueQuality } from "@hausmeter/domain";
import { bucketsBetween, floorToBucket, isDivisibleResolution, type Resolution } from "./grid.js";
import { worseQuality, worstQuality } from "./rollup.js";
import type { BucketValue, Series } from "./series.js";
import { startOfLocalDay, startOfLocalMonth, startOfNextLocalDay, startOfNextLocalMonth } from "./timezone.js";

/** Auf ein gröberes, festes Raster — 15 Minuten auf eine Stunde. */
export function resampleSum(series: Series, target: Resolution): Series {
  if (target === series.resolution) return series;
  if (!isDivisibleResolution(series.resolution, target)) {
    throw new Error(
      `Ein ${series.resolution}-Minuten-Raster lässt sich nicht auf ${target} Minuten verdichten.`,
    );
  }
  const perBucket = target / series.resolution;
  const groups = groupBy(series.values, (value) => floorToBucket(value.bucketStart, target).getTime());

  const values: BucketValue[] = [];
  for (const [start, members] of groups) {
    values.push(sumOf(new Date(start), members, members.length / perBucket));
  }

  return {
    resolution: target,
    range: { from: floorToBucket(series.range.from, target), to: series.range.to },
    values: values.sort((a, b) => a.bucketStart.getTime() - b.bucketStart.getTime()),
  };
}

/** Ein Tag im Ergebnis: Beginn und Ende in UTC, wie die Anlage ihn zählt. */
export interface LocalDay {
  readonly start: Date;
  readonly end: Date;
}

/** Die lokalen Tage, die ein Fenster berührt. */
export function localDaysBetween(from: Date, to: Date, timeZone: string): readonly LocalDay[] {
  return spansBetween(from, to, timeZone, startOfLocalDay, startOfNextLocalDay);
}

/** Die lokalen Monate, die ein Fenster berührt — für die Jahresansicht. */
export function localMonthsBetween(from: Date, to: Date, timeZone: string): readonly LocalDay[] {
  return spansBetween(from, to, timeZone, startOfLocalMonth, startOfNextLocalMonth);
}

function spansBetween(
  from: Date,
  to: Date,
  timeZone: string,
  startOf: (instant: Date, timeZone: string) => Date,
  startOfNext: (instant: Date, timeZone: string) => Date,
): readonly LocalDay[] {
  const spans: LocalDay[] = [];
  let start = startOf(from, timeZone);
  while (start < to) {
    const end = startOfNext(start, timeZone);
    spans.push({ start, end });
    start = end;
  }
  return spans;
}

export interface DailySeries {
  readonly days: readonly LocalDay[];
  readonly values: readonly BucketValue[];
}

/**
 * Auf lokale Kalendertage. Erwartet wird je Tag die volle Zahl seiner Buckets;
 * am ersten und letzten Tag des Fensters nur der Teil, der im Fenster liegt —
 * ein Fenster, das um 10 Uhr beginnt, hat den Vormittag nicht ausgelassen,
 * sondern nie gefragt.
 */
export function resampleToLocalDays(series: Series, timeZone: string): DailySeries {
  return resampleToSpans(series, localDaysBetween(series.range.from, series.range.to, timeZone), (instant) =>
    startOfLocalDay(instant, timeZone),
  );
}

/** Auf lokale Kalendermonate — dieselbe Regel wie bei Tagen: alle Buckets oder Lücke. */
export function resampleToLocalMonths(series: Series, timeZone: string): DailySeries {
  return resampleToSpans(series, localMonthsBetween(series.range.from, series.range.to, timeZone), (instant) =>
    startOfLocalMonth(instant, timeZone),
  );
}

function resampleToSpans(
  series: Series,
  days: readonly LocalDay[],
  startOf: (instant: Date) => Date,
): DailySeries {
  const byDay = groupBy(series.values, (value) => startOf(value.bucketStart).getTime());

  const values: BucketValue[] = [];
  for (const day of days) {
    const from = day.start > series.range.from ? day.start : series.range.from;
    const to = day.end < series.range.to ? day.end : series.range.to;
    const expected = bucketsBetween(from, to, series.resolution).length;
    const members = byDay.get(day.start.getTime()) ?? [];
    if (expected === 0 || members.length === 0) continue;
    values.push(sumOf(day.start, members, members.length / expected));
  }

  return { days, values };
}

/**
 * Die Summe über ein Fenster **mit** Abdeckung. `value` ist die Summe dessen,
 * was da ist; `coverage` sagt, wie viel das vom Fenster war. Beides zusammen ist
 * eine Aussage, die Summe allein nicht — und `null`, wenn gar nichts da ist.
 */
export function sumWithCoverage(series: Series): { value: number | null; coverage: number; quality: ValueQuality } {
  const expected = bucketsBetween(series.range.from, series.range.to, series.resolution).length;
  if (series.values.length === 0) return { value: null, coverage: 0, quality: "measured" };
  const value = series.values.reduce((total, entry) => total + entry.value, 0);
  // Ein verdichteter Wert zählt nur mit seinem Anteil: Ein Tag mit halber
  // Abdeckung ist ein halber Tag, kein ganzer.
  const present = series.values.reduce((total, entry) => total + (entry.coverage ?? 1), 0);
  return {
    value,
    coverage: expected === 0 ? 1 : Math.min(1, present / expected),
    // Eine Summe mit einem Ersatzwert darin ist eine Summe mit Ersatzwert (HM-115).
    quality: worstQuality(series.values),
  };
}

/**
 * Ein Verhältnis je Bucket aus zwei Reihen desselben Rasters — nur dort, wo
 * beide einen Wert haben und der Nenner nicht null ist. Division durch null
 * ergibt eine Lücke, keine 0 (siehe `@hausmeter/measurement-engine`).
 */
export function ratioSeries(numerator: Series, denominator: Series): Series {
  const den = new Map(denominator.values.map((value) => [value.bucketStart.getTime(), value]));
  const values: BucketValue[] = [];
  for (const num of numerator.values) {
    const other = den.get(num.bucketStart.getTime());
    if (other === undefined || other.value === 0) continue;
    /**
     * Zwei Teilsummen mit **verschiedener** Abdeckung ergeben kein Verhältnis:
     * 94 Viertelstunden Verbrauch gegen 96 Viertelstunden Erzeugung ist ein
     * Quotient aus zwei verschiedenen Zeiträumen. Wer das Verhältnis braucht,
     * schneidet beide Reihen vorher auf dieselben Buckets (`intersectSeries`).
     */
    if ((num.coverage ?? 1) !== (other.coverage ?? 1)) continue;
    values.push({
      bucketStart: num.bucketStart,
      value: num.value / other.value,
      quality: worseQuality(num.quality, other.quality),
      ...(num.coverage === undefined ? {} : { coverage: num.coverage }),
    });
  }
  return { resolution: numerator.resolution, range: numerator.range, values };
}

/**
 * Das Verhältnis der **Summen** über ein Fenster — nicht das Mittel der
 * Bucket-Verhältnisse. Gerechnet wird nur über Buckets, die beide Reihen
 * kennen: Ein Eigenverbrauch ohne den Verbrauch derselben Viertelstunde
 * verfälschte den Quotienten nach oben.
 */
export function ratioOfSums(
  numerator: Series,
  denominator: Series,
): { value: number | null; coverage: number; quality: ValueQuality } {
  const den = new Map(denominator.values.map((value) => [value.bucketStart.getTime(), value]));
  let top = 0;
  let bottom = 0;
  let shared = 0;
  let quality: ValueQuality = "measured";
  for (const num of numerator.values) {
    const other = den.get(num.bucketStart.getTime());
    if (other === undefined) continue;
    top += num.value;
    bottom += other.value;
    shared += 1;
    quality = worseQuality(quality, worseQuality(num.quality, other.quality));
  }
  const expected = bucketsBetween(numerator.range.from, numerator.range.to, numerator.resolution).length;
  const coverage = expected === 0 ? 1 : Math.min(1, shared / expected);
  if (shared === 0 || bottom === 0) return { value: null, coverage, quality };
  return { value: top / bottom, coverage, quality };
}

/**
 * Zwei Reihen auf die Buckets beschränkt, die **beide** tragen.
 *
 * Für ein Verhältnis zweier verdichteter Reihen: Erst schneiden, dann
 * verdichten — dann haben Zähler und Nenner je Abschnitt dieselbe Abdeckung,
 * und der Quotient gilt für denselben Zeitraum.
 */
export function intersectSeries(a: Series, b: Series): [Series, Series] {
  const inA = new Set(a.values.map((value) => value.bucketStart.getTime()));
  const inB = new Set(b.values.map((value) => value.bucketStart.getTime()));
  return [
    { ...a, values: a.values.filter((value) => inB.has(value.bucketStart.getTime())) },
    { ...b, values: b.values.filter((value) => inA.has(value.bucketStart.getTime())) },
  ];
}

/**
 * Die Summe der Mitglieder, mit Abdeckung, wenn welche fehlen (HM-105).
 *
 * **Eine Teilsumme ist keine Lücke — und keine volle Summe.** Bis HM-105 war
 * ein Tag mit 94 von 96 Viertelstunden eine Lücke: Er verschwand aus der
 * Wochen- und Monatsansicht, als wäre den ganzen Tag nichts gemessen worden;
 * und im Jahr war kein Monat je vollständig, weil der laufende es nie sein
 * kann — die Ansicht blieb leer, mit tausenden Zeilen dahinter. Die Summe
 * dessen, was da ist, **mit** dem Anteil daneben, ist die Aussage, die die
 * Kacheln seit HM-012 machen; die Balken machen jetzt dieselbe.
 */
function sumOf(bucketStart: Date, members: readonly BucketValue[], coverage: number): BucketValue {
  return {
    bucketStart,
    value: members.reduce((total, entry) => total + entry.value, 0),
    quality: members.reduce<ValueQuality>((worst, entry) => worseQuality(worst, entry.quality), "measured"),
    ...(coverage >= 1 ? {} : { coverage }),
  };
}

function groupBy(
  values: readonly BucketValue[],
  keyOf: (value: BucketValue) => number,
): Map<number, BucketValue[]> {
  const groups = new Map<number, BucketValue[]>();
  for (const value of values) {
    const key = keyOf(value);
    const group = groups.get(key);
    if (group === undefined) groups.set(key, [value]);
    else group.push(value);
  }
  return groups;
}
